import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { X, Loader2, Upload, Eye, Printer } from "lucide-react";
import { API_URL } from "@/utils/apiConfig";

export const ReceiptConfigTab = ({ authAxios, restaurant, onUpdate }) => {
  const rc = restaurant?.receipt_config || {};
  const [companyName, setCompanyName] = useState(rc.company_name || restaurant?.name || "");
  const [nif, setNif] = useState(rc.nif || "");
  const [address, setAddress] = useState(rc.address || "");
  const [footerMessage, setFooterMessage] = useState(rc.footer_message || "Obrigado pela preferencia!");
  const [logoUrl, setLogoUrl] = useState(rc.logo_url || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Granular print toggles
  const [printOptions, setPrintOptions] = useState({
    show_store_name: rc.show_store_name ?? true,
    show_cif: rc.show_cif ?? true,
    show_order_number: rc.show_order_number ?? true,
    show_order_channel: rc.show_order_channel ?? true,
    show_delivery_estimate: rc.show_delivery_estimate ?? true,
    show_customer_notes: rc.show_customer_notes ?? true,
    show_delivery_data: rc.show_delivery_data ?? true,
    show_order_position: rc.show_order_position ?? true,
    show_customer_phone: rc.show_customer_phone ?? true,
    show_payment_method: rc.show_payment_method ?? true,
    show_item_prices: rc.show_item_prices ?? true,
    auto_print_new_orders: rc.auto_print_new_orders ?? false,
    print_kitchen_ticket: rc.print_kitchen_ticket ?? false,
  });

  const toggleOption = (key) => {
    setPrintOptions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const BACKEND = API_URL;

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Imagem muito grande (max 2MB)"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await authAxios.post("/restaurant/upload-logo", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoUrl(res.data.logo_url);
      toast.success("Logo enviado");
      onUpdate();
    } catch { toast.error("Erro ao enviar logo"); }
    setUploading(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await authAxios.patch("/restaurant", {
        receipt_config: {
          company_name: companyName, nif, address, footer_message: footerMessage, logo_url: logoUrl,
          ...printOptions,
        }
      });
      toast.success("Configuracao do recibo salva");
      onUpdate();
    } catch { toast.error("Erro ao salvar"); }
    setSaving(false);
  };

  const handleTestPrint = () => {
    const logoSrc = logoUrl ? (logoUrl.startsWith("http") ? logoUrl : `${BACKEND}${logoUrl}`) : "";
    const logoHtml = logoSrc ? `<div style="text-align:center;margin-bottom:4px"><img src="${logoSrc}" style="max-width:50mm;max-height:20mm;object-fit:contain" /></div>` : "";
    const html = `<!DOCTYPE html><html><head><title>Test Receipt</title>
<style>@page{size:80mm auto;margin:0}body{font-family:'Courier New',monospace;width:72mm;margin:4mm;font-size:11px;color:#000}
h2{text-align:center;font-size:14px;margin:4px 0}p.center{text-align:center;margin:2px 0}
hr{border:none;border-top:1px dashed #000;margin:6px 0}
table{width:100%;border-collapse:collapse}td{padding:2px 0}
.total{font-size:14px;font-weight:bold}.right{text-align:right}</style></head>
<body>${logoHtml}${printOptions.show_store_name ? `<h2>${companyName || "Meu Restaurante"}</h2>` : ""}${printOptions.show_cif && nif ? `<p class="center">NIF: ${nif}</p>` : ""}${address ? `<p class="center" style="font-size:9px">${address}</p>` : ""}<hr/>
${printOptions.show_order_number ? `<p><b>Pedido:</b> #DEMO1234</p>` : ""}<p><b>Data:</b> ${new Date().toLocaleString("pt-BR")}</p><p><b>Cliente:</b> Joao Exemplo</p>${printOptions.show_customer_phone ? `<p><b>Tel:</b> +351 912345678</p>` : ""}${printOptions.show_order_channel ? `<p><b>Canal:</b> Delivery</p>` : ""}${printOptions.show_delivery_data ? `<p><b>Endereco:</b> Rua das Flores 42, Lisboa</p>` : ""}<hr/>
<table><tr><td>2x Pizza Margherita</td><td style="text-align:right">${printOptions.show_item_prices ? "18.00EUR" : ""}</td></tr><tr><td>1x Coca-Cola</td><td style="text-align:right">${printOptions.show_item_prices ? "2.50EUR" : ""}</td></tr></table><hr/>
<table><tr><td>Subtotal</td><td class="right">20.50EUR</td></tr><tr class="total"><td>TOTAL</td><td class="right">23.00EUR</td></tr></table><hr/>
${printOptions.show_payment_method ? `<p class="center"><b>Pagamento:</b> Mastercard</p>` : ""}
${printOptions.show_customer_notes ? `<p class="center" style="font-size:10px;color:#666">OBS: Sem cebola</p>` : ""}
<p class="center" style="margin-top:8px;font-size:10px">${footerMessage}</p>
<script>window.onload=function(){window.print();}</script></body></html>`;
    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open(); doc.write(html); doc.close();
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => { try { document.body.removeChild(iframe); } catch {} }, 3000);
  };

  const PRINT_TOGGLES = [
    { key: "show_store_name", label: "Nome da loja", desc: "Exibir nome da empresa no topo do recibo" },
    { key: "show_cif", label: "NIF / CIF", desc: "Exibir numero de identificacao fiscal" },
    { key: "show_order_number", label: "Numero do pedido", desc: "Exibir # do pedido no recibo" },
    { key: "show_order_channel", label: "Canal do pedido", desc: "Exibir origem (Delivery, Mesa, Retirada)" },
    { key: "show_customer_phone", label: "Telefone do cliente", desc: "Incluir telefone no recibo" },
    { key: "show_delivery_data", label: "Dados de entrega", desc: "Exibir endereco de entrega" },
    { key: "show_delivery_estimate", label: "Estimativa de entrega", desc: "Tempo estimado de entrega" },
    { key: "show_customer_notes", label: "Observacoes do cliente", desc: "Incluir notas/observacoes" },
    { key: "show_item_prices", label: "Precos dos itens", desc: "Exibir preco individual de cada item" },
    { key: "show_payment_method", label: "Forma de pagamento", desc: "Exibir metodo de pagamento" },
    { key: "show_order_position", label: "Posicao na fila", desc: "Numero sequencial do pedido no dia" },
    { key: "auto_print_new_orders", label: "Imprimir automaticamente", desc: "Imprimir novos pedidos ao receber" },
    { key: "print_kitchen_ticket", label: "Ticket de cozinha", desc: "Imprimir ticket separado para cozinha (sem precos)" },
  ];

  return (
    <div className="space-y-6">
      <Card className="bg-card border-border/50 border-l-4 border-l-primary">
        <CardHeader>
          <CardTitle className="uppercase">Dados do Recibo</CardTitle>
          <CardDescription>Personalize as informacoes da empresa que aparecem nos recibos.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Logo da empresa</Label>
            <div className="flex items-center gap-4">
              {logoUrl && (
                <div className="w-24 h-16 border rounded-lg overflow-hidden bg-white flex items-center justify-center">
                  <img src={`${BACKEND}${logoUrl}`} alt="Logo" className="max-w-full max-h-full object-contain" />
                </div>
              )}
              <div>
                <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 border border-dashed border-border rounded-lg hover:border-primary/50 transition-colors text-sm text-muted-foreground hover:text-foreground" data-testid="receipt-logo-upload">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {logoUrl ? "Trocar logo" : "Enviar logo"}
                  <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                </label>
                <p className="text-xs text-muted-foreground mt-1">PNG, JPG. Max 2MB</p>
              </div>
              {logoUrl && (
                <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setLogoUrl("")} data-testid="remove-receipt-logo">
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
          <Separator />
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Nome da empresa</Label><Input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Kaiso Sushi" className="bg-secondary border-transparent" data-testid="receipt-company-name" /></div>
            <div className="space-y-2"><Label>NIF / CIF</Label><Input value={nif} onChange={e => setNif(e.target.value)} placeholder="B22473664" className="bg-secondary border-transparent" data-testid="receipt-nif" /></div>
          </div>
          <div className="space-y-2"><Label>Endereco</Label><Input value={address} onChange={e => setAddress(e.target.value)} placeholder="Rua Carlos Reis 43A, Lisboa" className="bg-secondary border-transparent" data-testid="receipt-address" /></div>
          <div className="space-y-2"><Label>Mensagem do rodape</Label><Input value={footerMessage} onChange={e => setFooterMessage(e.target.value)} placeholder="Obrigado pela preferencia!" className="bg-secondary border-transparent" data-testid="receipt-footer" /></div>
        </CardContent>
      </Card>

      {/* Granular Print Toggles */}
      <Card className="bg-card border-border/50 border-l-4 border-l-blue-500">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Printer className="w-5 h-5 text-blue-500" />
            <div>
              <CardTitle className="uppercase">Opcoes de Impressao</CardTitle>
              <CardDescription>Configure o que aparece no recibo impresso. Igual ao Menu Integrado.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {PRINT_TOGGLES.map(toggle => (
              <div key={toggle.key} className="flex items-center justify-between py-2 border-b border-border/30 last:border-0" data-testid={`print-toggle-${toggle.key}`}>
                <div className="flex-1 min-w-0 mr-4">
                  <p className="text-sm font-medium text-foreground">{toggle.label}</p>
                  <p className="text-xs text-muted-foreground">{toggle.desc}</p>
                </div>
                <Switch
                  checked={printOptions[toggle.key]}
                  onCheckedChange={() => toggleOption(toggle.key)}
                  data-testid={`switch-${toggle.key}`}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3">
        <Button className="glow-orange" onClick={save} disabled={saving} data-testid="save-receipt-config">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Salvar Configuracao
        </Button>
        <Button variant="outline" onClick={handleTestPrint} data-testid="test-receipt-print"><Eye className="w-4 h-4 mr-2" /> Visualizar Recibo</Button>
      </div>
    </div>
  );
};
